import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDashboardStore } from '@store'
import {
	StatCard,
	TaskList,
	DeadlineList,
	ProgressCard,
	QuickActions
} from '@components/features/dashboard/widgets'
import {
	ProgressRing,
	Sparkline
} from '@components/features/dashboard/charts'
import {
	DashboardGrid,
	WidgetContainer,
	StatsRow
} from '@components/features/dashboard/layouts'
import './Dashboard.scss'

/**
 * Employee Dashboard
 *
 * Focused view for employees showing their assigned tasks,
 * upcoming deadlines, training progress, and personal metrics.
 */
const EmployeeDashboard = () => {
	const navigate = useNavigate()

	// Dashboard store
	const {
		employeeOverview,
		assignedTasks,
		trainingProgress,
		deadlines,
		loading,
		error,
		autoRefreshEnabled,
		fetchEmployeeDashboard,
		startAutoRefresh,
		stopAutoRefresh,
		getTimeSinceUpdate
	} = useDashboardStore()

	// Fetch dashboard data on mount
	useEffect(() => {
		fetchEmployeeDashboard()

		// Start auto-refresh every 60 seconds
		const cleanup = startAutoRefresh('employee', 60000)

		return () => {
			cleanup()
			stopAutoRefresh()
		}
	}, [fetchEmployeeDashboard, startAutoRefresh, stopAutoRefresh])

	// Manual refresh handler
	const handleRefresh = useCallback(() => {
		fetchEmployeeDashboard()
	}, [fetchEmployeeDashboard])

	// Quick action handlers
	const quickActions = [
		{
			icon: '📝',
			label: 'My Tasks',
			onClick: () => navigate('/tasks/my-tasks'),
			permission: null
		},
		{
			icon: '📚',
			label: 'Training',
			onClick: () => navigate('/training'),
			permission: null
		},
		{
			icon: '📁',
			label: 'My Projects',
			onClick: () => navigate('/projects'),
			permission: null
		},
		{
			icon: '❓',
			label: 'Help & Support',
			onClick: () => navigate('/support'),
			permission: null
		}
	]

	// Format tasks for TaskList
	// Note: TaskList expects due_date for date display, status for workflow_status
	const taskItems = (assignedTasks || []).map(task => ({
		id: task.id,
		title: task.title || task.name,
		project: task.project_name,
		status: task.workflow_status || task.status,
		priority: task.priority || 'medium',
		due_date: task.due_date,
		progress: task.progress || 0
	}))

	// Priority tasks (high priority or due soon)
	const priorityTasks = taskItems.filter(
		task => task.priority === 'high' || isWithinDays(task.due_date, 3)
	)

	// Format deadlines for DeadlineList
	// Note: DeadlineList expects due_date (not dueDate) for date display
	const upcomingDeadlines = (deadlines || []).slice(0, 5).map(deadline => ({
		id: deadline.id,
		title: deadline.title || deadline.name,
		due_date: deadline.due_date,
		type: deadline.type || 'task',
		project: deadline.project_name
	}))

	// Training modules progress
	const trainingModules = (trainingProgress?.modules || []).map(module => ({
		name: module.name,
		progress: module.progress || 0,
		totalItems: module.total_items || 0,
		completedItems: module.completed_items || 0
	}))

	// Productivity trend data for sparkline
	const productivityTrend = employeeOverview?.productivity_trend || []

	return (
		<div className="dashboard-page employee-dashboard">
			{/* Page Header */}
			<div className="page-header">
				<div className="page-header__content">
					<div>
						<h1 className="page-title">My Dashboard</h1>
						<nav className="breadcrumb">
							<span className="breadcrumb__item breadcrumb__item--active">Dashboard</span>
						</nav>
					</div>
					<div className="page-header__actions">
						<span className="last-updated">
							Last updated: {getTimeSinceUpdate()}
							{autoRefreshEnabled && <span className="auto-refresh-badge">Auto</span>}
						</span>
						<button
							className="btn btn--secondary btn--sm"
							onClick={handleRefresh}
							disabled={loading}
						>
							{loading ? 'Refreshing...' : 'Refresh'}
						</button>
					</div>
				</div>
			</div>

			{/* Error Display */}
			{error && (
				<div className="alert alert--danger">
					<p>{error}</p>
					<button onClick={handleRefresh}>Retry</button>
				</div>
			)}

			{/* Stats Row */}
			<StatsRow>
				<StatCard
					title="My Tasks"
					value={employeeOverview?.total_tasks ?? '-'}
					subtitle="Assigned to you"
					icon="📝"
					variant="primary"
					loading={loading}
					onClick={() => navigate('/tasks/my-tasks')}
				/>
				<StatCard
					title="Due This Week"
					value={employeeOverview?.due_this_week ?? '-'}
					subtitle="Upcoming deadlines"
					icon="📅"
					variant="warning"
					loading={loading}
					onClick={() => navigate('/tasks/my-tasks?due=this_week')}
				/>
				<StatCard
					title="Completed"
					value={employeeOverview?.completed_this_month ?? '-'}
					subtitle="This month"
					icon="✅"
					variant="success"
					loading={loading}
				/>
				<StatCard
					title="Overdue"
					value={employeeOverview?.overdue_tasks ?? '-'}
					subtitle="Need attention"
					icon="⚠️"
					variant="danger"
					loading={loading}
					onClick={() => navigate('/tasks/my-tasks?status=overdue')}
				/>
			</StatsRow>

			{/* Priority Tasks and Progress Row */}
			<DashboardGrid columns={2}>
				{/* Priority Tasks */}
				<WidgetContainer
					title="Priority Tasks"
					onAction={() => navigate('/tasks/my-tasks?priority=high')}
					actionLabel="View All"
					loading={loading}
				>
					<TaskList
						tasks={priorityTasks.slice(0, 5)}
						onTaskClick={(task) => navigate(`/tasks/${task.id}`)}
						showPriority={true}
						showDueDate={true}
						loading={loading}
						emptyMessage="No priority tasks"
					/>
				</WidgetContainer>

				{/* My Progress */}
				<WidgetContainer
					title="My Progress"
					loading={loading}
				>
					<div className="my-progress-container">
						<div className="my-progress__ring">
							<ProgressRing
								value={employeeOverview?.completion_rate || 0}
								max={100}
								size={140}
								strokeWidth={12}
								showPercentage={true}
								label="Completion Rate"
								loading={loading}
							/>
						</div>
						<div className="my-progress__details">
							<div className="progress-detail">
								<span className="progress-detail__label">Tasks Completed</span>
								<span className="progress-detail__value">
									{employeeOverview?.tasks_completed || 0} / {employeeOverview?.total_assigned || 0}
								</span>
							</div>
							<div className="progress-detail">
								<span className="progress-detail__label">On-Time Rate</span>
								<span className="progress-detail__value">
									{employeeOverview?.on_time_rate || 0}%
								</span>
							</div>
							<div className="progress-detail">
								<span className="progress-detail__label">Productivity</span>
								<div className="progress-detail__sparkline">
									<Sparkline
										data={productivityTrend}
										dataKey="value"
										height={30}
										width={100}
										color="var(--color-primary)"
										loading={loading}
									/>
								</div>
							</div>
						</div>
					</div>
				</WidgetContainer>
			</DashboardGrid>

			{/* Deadlines and Training Row */}
			<DashboardGrid columns={2}>
				{/* Upcoming Deadlines */}
				<WidgetContainer
					title="Upcoming Deadlines"
					onAction={() => navigate('/tasks/my-tasks?sort=due_date')}
					actionLabel="View All"
					loading={loading}
				>
					<DeadlineList
						deadlines={upcomingDeadlines}
						onDeadlineClick={(deadline) => navigate(`/tasks/${deadline.id}`)}
						showProject={true}
						loading={loading}
						emptyMessage="No upcoming deadlines"
					/>
				</WidgetContainer>

				{/* Training Progress */}
				<WidgetContainer
					title="Training Progress"
					onAction={() => navigate('/training')}
					actionLabel="Continue"
					loading={loading}
				>
					<div className="training-progress-container">
						{trainingModules.length === 0 ? (
							<div className="empty-state">
								<span className="empty-state__icon">📚</span>
								<p>No training modules assigned</p>
							</div>
						) : (
							<>
								<div className="training-overall">
									<ProgressRing
										value={trainingProgress?.overall_progress || 0}
										max={100}
										size={100}
										strokeWidth={10}
										showPercentage={true}
										label="Overall"
										loading={loading}
									/>
								</div>
								<div className="training-modules">
									{trainingModules.slice(0, 3).map((module, index) => (
										<ProgressCard
											key={index}
											title={module.name}
											current={module.completedItems}
											total={module.totalItems}
											percentage={module.progress}
											status={module.progress === 100 ? 'success' : module.progress >= 50 ? 'warning' : 'default'}
											compact={true}
										/>
									))}
								</div>
							</>
						)}
					</div>
				</WidgetContainer>
			</DashboardGrid>

			{/* All Tasks Section */}
			<DashboardGrid columns={1}>
				<WidgetContainer
					title="All My Tasks"
					onAction={() => navigate('/tasks/my-tasks')}
					actionLabel="View All"
					loading={loading}
				>
					<TaskList
						tasks={taskItems.slice(0, 8)}
						onTaskClick={(task) => navigate(`/tasks/${task.id}`)}
						showPriority={true}
						showDueDate={true}
						showProgress={true}
						loading={loading}
						emptyMessage="No tasks assigned"
					/>
				</WidgetContainer>
			</DashboardGrid>

			{/* Quick Actions */}
			<QuickActions actions={quickActions} />
		</div>
	)
}

/**
 * Check if a date is within the specified number of days from today
 */
function isWithinDays(dateStr, days) {
	if (!dateStr) return false
	const date = new Date(dateStr)
	const today = new Date()
	const diffTime = date.getTime() - today.getTime()
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
	return diffDays >= 0 && diffDays <= days
}

export default EmployeeDashboard
